/**
 * 默认设计令牌配置
 * 提供标准的设计系统基线
 */

import type { DesignTokens } from './types';

/**
 * 默认设计令牌
 * 基于现代设计系统最佳实践
 */
export const defaultTokens: DesignTokens = {
  /**
   * 颜色系统
   */
  colors: {
    // 品牌色 - 蓝色系
    primary: '#08C4C8',
    primaryHover: '#1d4ed8',
    primaryActive: '#1e40af',
    primaryDisabled: '#93c5fd',

    // 功能色
    success: '#10b981',
    successHover: '#059669',
    warning: '#f59e0b',
    warningHover: '#d97706',
    error: '#ef4444',
    errorHover: '#dc2626',
    info: '#3b82f6',
    infoHover: '#2563eb',

    // 文本颜色
    text: {
      primary: '#0f172a',          // gray-900
      secondary: '#475569',        // gray-600
      tertiary: '#64748b',         // gray-500
      disabled: 'rgba(15, 23, 42, 0.35)',
      inverse: '#ffffff',
      placeholder: '#94a3b8',      // gray-400
    },

    // 边框颜色
    border: {
      base: '#e2e8f0',    // gray-200
      light: '#f1f5f9',   // gray-100
      dark: '#cbd5e1',    // gray-300
      focus: '#08C4C8',   // primary
    },

    // 背景颜色
    background: {
      base: '#f8fafc',      // gray-50
      elevated: '#ffffff',
      hover: '#f1f5f9',     // gray-100
      active: '#e2e8f0',    // gray-200
      overlay: 'rgba(0, 0, 0, 0.45)',
      disabled: '#f8fafc',  // gray-50
    },
  },

  /**
   * 字体排印
   */
  typography: {
    // 字体族
    fontFamily: {
      base: `-apple-system, BlinkMacSystemFont, 'SF Pro', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif`,
      heading: `-apple-system, BlinkMacSystemFont, 'SF Pro', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif`,
      code: `'SF Mono', 'Monaco', 'Consolas', 'Liberation Mono', 'Courier New', monospace`,
    },

    // 字体大小
    fontSize: {
      xs: '12px',
      sm: '14px',
      base: '16px',
      lg: '18px',
      xl: '20px',
      '2xl': '24px',
      '3xl': '32px',
      '4xl': '40px',
    },

    // 字重
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },

    // 行高
    lineHeight: {
      tight: 1.2,
      base: 1.5,
      relaxed: 1.75,
      loose: 2,
    },
  },

  /**
   * 间距
   */
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    '2xl': '48px',
    '3xl': '64px',
  },

  /**
   * 圆角
   */
  borderRadius: {
    none: '0',
    sm: '4px',
    base: '6px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    '2xl': '24px',
    full: '9999px',
  },

  /**
   * 阴影
   */
  shadow: {
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
  },
};

/**
 * 创建自定义令牌
 * 支持部分覆盖默认令牌
 */
export function createTokens(overrides?: Partial<DesignTokens>): DesignTokens {
  if (!overrides) {
    return { ...defaultTokens };
  }

  return {
    colors: {
      ...defaultTokens.colors,
      ...overrides.colors,
      text: {
        ...defaultTokens.colors.text,
        ...overrides.colors?.text,
      },
      border: {
        ...defaultTokens.colors.border,
        ...overrides.colors?.border,
      },
      background: {
        ...defaultTokens.colors.background,
        ...overrides.colors?.background,
      },
    },
    typography: {
      ...defaultTokens.typography,
      ...overrides.typography,
      fontFamily: {
        ...defaultTokens.typography.fontFamily,
        ...overrides.typography?.fontFamily,
      },
      fontSize: {
        ...defaultTokens.typography.fontSize,
        ...overrides.typography?.fontSize,
      },
      fontWeight: {
        ...defaultTokens.typography.fontWeight,
        ...overrides.typography?.fontWeight,
      },
      lineHeight: {
        ...defaultTokens.typography.lineHeight,
        ...overrides.typography?.lineHeight,
      },
    },
    spacing: {
      ...defaultTokens.spacing,
      ...overrides.spacing,
    },
    borderRadius: {
      ...defaultTokens.borderRadius,
      ...overrides.borderRadius,
    },
    shadow: {
      ...defaultTokens.shadow,
      ...overrides.shadow,
    },
    other: overrides.other,
  };
}
